const CAP25 = {
  id: 25,
  slug: "cap25-como-funciona-la-cadena-de-bloques",
  title: "¿Cómo funciona la cadena de bloques?",
  sections: [
    {
      heading: "Introducción",
      text: [
        "El funcionamiento de la tecnología blockchain pasa por una serie de procesos que van desde la creación de una transacción hasta su inclusión en un bloque válido dentro de la cadena de bloques.",
        "En esta lección aprenderemos sobre los mecanismos que hacen posible esta tecnología innovadora."
      ]
    },
    {
      heading: "Recordemos que:",
      text: [
        "Una cadena de bloques es un **libro mayor digital descentralizado** que almacena información en bloques interconectados entre sí.",
        "Cada bloque contiene un conjunto de transacciones y datos, y está **vinculado criptográficamente al bloque anterior**, formando una cadena.",
        "Gracias a esta estructura, la blockchain registra información de manera **inmutable y segura**, permitiendo que los nodos que conforman la red solo puedan **agregar información por consenso**.",
        "En otras palabras, **la información registrada es permanente, transparente y no puede ser alterada**."
      ]
    },
    {
      heading: "Funcionamiento de una cadena de bloques",
      text: [
        "El funcionamiento de una cadena de bloques se puede dividir de la siguiente manera:"
      ],
      subsections: [
        {
          heading: "1. Creación de una transacción",
          text: [
            "Todo comienza cuando un usuario, digamos María, decide enviar criptomonedas a otro usuario, José.",
            "María crea una transacción que incluye:",
            "🔹 La cantidad de criptomonedas a transferir.",
            "🔹 La dirección pública de José.",
            "🔹 Su **firma digital**, generada con su **clave privada**, que certifica que ella es la propietaria de los fondos."
          ]
        },
        {
          heading: "2. Transmisión de la transacción",
          text: [
            "Una vez creada, María **envía la transacción a la red**.",
            "Esta se **difunde a todos los nodos** (computadoras conectadas a la red blockchain).",
            "En una blockchain pública como Bitcoin o Ethereum, cualquier persona puede ser un nodo y validar transacciones."
          ]
        },
        {
          heading: "3. Validación de la transacción",
          text: [
            "Los nodos completos reciben la transacción y comienzan a verificarla.",
            "Revisan que:",
            "🔹 María realmente tenga fondos suficientes.",
            "🔹 No esté intentando realizar un **doble gasto**.",
            "🔹 La transacción cumple con las reglas del protocolo.",
            "Si es válida, los nodos la aceptan y la guardan en la **mempool**, una lista de transacciones pendientes."
          ]
        },
        {
          heading: "4. Minado o validación de un bloque",
          text: [
            "Los nodos llamados **mineros** (en PoW) o **validadores** (en PoS) agrupan varias transacciones en un bloque.",
            "En el caso de Proof of Work (PoW), los mineros compiten entre sí para **resolver un problema matemático complejo** que les permitirá proponer el nuevo bloque.",
            "Este proceso se llama **minería**, y requiere capacidad de cómputo y energía."
          ]
        },
        {
          heading: "5. Creación del hash",
          text: [
            "Para proponer un bloque válido, el minero debe encontrar un **hash** que cumpla con ciertas condiciones del protocolo.",
            "Usa un número llamado **nonce** combinado con los datos del bloque.",
            "Cada vez que cambia el nonce, el hash cambia completamente.",
            "El primer minero que encuentra un hash válido envía el bloque a la red y recibe una recompensa en criptomonedas."
          ]
        },
        {
          heading: "6. Inclusión del bloque en la cadena",
          text: [
            "Una vez transmitido, los demás nodos verifican el bloque.",
            "Si es válido, lo **añaden a la cadena de bloques**, vinculándolo mediante su hash al bloque anterior.",
            "Así se forma una **cadena de bloques inmutable**."
          ]
        },
        {
          heading: "7. Confirmación de la transacción",
          text: [
            "Cuando el bloque es aceptado e integrado, la transacción de María a José se considera **confirmada e irreversible**.",
            "A partir de ese momento, queda **registrada de forma permanente y pública en la blockchain**."
          ]
        }
      ]
    },
    {
      heading: "¿Qué ocurre después de la confirmación?",
      text: [
        "Una vez confirmado un nuevo bloque:",
        "🔹 Cada nodo actualiza su copia de la blockchain.",
        "🔹 Todos los participantes tienen la misma versión de la cadena.",
        "🔹 Se garantiza la **integridad, transparencia y seguridad de los datos**."
      ]
    },
    {
      heading: "Ejemplo simple de una transacción en blockchain",
      text: [
        "Imaginemos que María quiere enviar **2 bitcoins** a José:",
        "🔹 María crea la transacción y la firma con su **clave privada**.",
        "🔹 La transacción se transmite a la red y llega a los nodos.",
        "🔹 Los nodos validan que María tiene los fondos.",
        "🔹 Los mineros incluyen la transacción en un nuevo bloque y compiten para encontrar el hash correcto.",
        "🔹 El bloque se añade a la cadena y la transacción queda confirmada.",
        "🔹 María y José pueden **ver la transacción en el explorador de bloques** (como Blockchain.com o Blockchair)."
      ]
    },
    {
      heading: "Conclusión",
      text: [
        "Este proceso muestra cómo blockchain permite realizar transacciones de forma **descentralizada, segura, transparente y sin intermediarios**.",
        "Una vez confirmada, la información es **pública, permanente e inalterable**.",
        "Ahora que entendemos cómo funciona el registro de transacciones, veamos los **tipos de blockchain que existen: pública, privada, híbrida o de consorcio**."
      ]
    }
  ]
};

export default CAP25;
